import { AlertCircle } from "lucide-react";
import { LIFECYCLE_GROUP, type LifecycleGroup, lifecycleLabel } from "../lifecycleStates";
import type { AnimalDetail, AnimalRetiredReason, AnimalSex, LifecycleState } from "../types";
import { formatDate } from "../utils/formatDate";
import { ProducingSubStateBadge } from "./ProducingSubStateBadge";

type Props = {
  animal: AnimalDetail;
};

export function AnimalDetailHeader({ animal }: Props) {
  return (
    <header className="space-y-3">
      {animal.retiredAt && (
        <RetiredBanner retiredAt={animal.retiredAt} reason={animal.retiredReason} />
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="font-medium font-mono text-[28px] text-app-ink-1 leading-none tracking-tight">
          {animal.tag}
        </h1>
        <SexIcon sex={animal.sex} />
        {animal.name && <p className="text-[18px] text-app-ink-2">{animal.name}</p>}
        <LifecycleBadge state={animal.lifecycleState} sex={animal.sex} />
        <ProducingSubStateBadge animal={animal} />
      </div>
    </header>
  );
}

const RETIRED_REASON_LABEL: Record<AnimalRetiredReason, string> = {
  died: "Fallecimiento",
  culled: "Descarte",
  sold: "Venta",
};

function RetiredBanner({
  retiredAt,
  reason,
}: {
  retiredAt: string;
  reason: AnimalRetiredReason | null;
}) {
  const reasonLabel = reason ? RETIRED_REASON_LABEL[reason] : "Retirado";
  return (
    // biome-ignore lint/a11y/useSemanticElements: <output> mapea a role="output", no a status. role="status" es la live-region polite correcta para anunciar el estado terminal del animal.
    <div
      role="status"
      className="flex items-start gap-2 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-app-ink-2 text-sm"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
      <p>
        Animal retirado · <span className="font-medium">{reasonLabel}</span> el{" "}
        <span className="font-mono">{formatDate(retiredAt)}</span>
      </p>
    </div>
  );
}

function SexIcon({ sex }: { sex: AnimalSex }) {
  if (sex === "female") {
    return (
      <span aria-label="Hembra" className="font-medium text-[20px] text-violet-500">
        ♀
      </span>
    );
  }
  return (
    <span aria-label="Macho" className="font-medium text-[20px] text-blue-500">
      ♂
    </span>
  );
}

const GROUP_CLASSES: Record<LifecycleGroup, string> = {
  cria: "bg-blue-100 text-blue-700",
  desarrollo: "bg-violet-100 text-violet-700",
  productiva: "bg-app-emerald-bg text-app-emerald",
  retirada: "bg-neutral-100 text-neutral-500",
};

function LifecycleBadge({ state, sex }: { state: LifecycleState; sex: AnimalSex }) {
  const group = LIFECYCLE_GROUP[state];
  return (
    <span
      className={`inline-block rounded-xl px-2.5 py-1 font-medium text-[12px] ${GROUP_CLASSES[group]}`}
    >
      {lifecycleLabel(state, sex)}
    </span>
  );
}
